// @flow

import React, { PureComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import moment from 'moment';
import styled from 'styled-components';
import type { RouterHistory } from 'react-router';
import { i18n } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import DeleteShipmentMutation from 'main-app/mutations/DeleteShipment';
import Table from 'shared/components/common/Table';
import ShipmentStatusPill from 'shared/components/common/Pill/ShipmentStatusPill';

const Wrapper = styled.div`
  padding: 0 24px;
`;

type Props = {
  history: RouterHistory,
  shipmentEdges: Array<Object>,
  deleteEnabled: boolean,
  editEnabled: boolean,
  onCreateNew: () => void,
};

class ShipmentsTable extends PureComponent<Props> {
  handleRowClick = (rowData: Object) => {
    const { history } = this.props;
    history.push(`/shipment/${rowData.node.id}`);
  };

  handleDelete = (shipment: Object) => {
    DeleteShipmentMutation.commit({
      variables: { input: { id: shipment.id } },
      onCompleted: () => {
        Actions.alertNotification(i18n.t('Shipment deleted successfully.'), 'success');
      },
      onError: () => {
        Actions.alertNotification(i18n.t('Error deleting shipment.'), 'error');
      },
    });
  };

  getColumns = () => {
    const columns = [
      {
        Header: i18n.t('Shipment #'),
        accessor: 'node.shipmentNumber',
        width: 130,
      },
      {
        Header: i18n.t('Order #'),
        accessor: 'node.order.orderNumber',
        width: 120,
      },
      {
        Header: i18n.t('Customer'),
        accessor: 'node.customer.name',
        minWidth: 160,
      },
      {
        Header: i18n.t('Status'),
        accessor: 'node.status',
        width: 140,
        Cell: ({ value }: *) => <ShipmentStatusPill status={value} />,
      },
      {
        Header: i18n.t('Carrier'),
        accessor: 'node.carrierName',
        width: 140,
      },
      {
        Header: i18n.t('Tracking #'),
        accessor: 'node.trackingNumber',
        minWidth: 160,
      },
      {
        Header: i18n.t('Est. Ship Date'),
        accessor: 'node.estimatedShipDate',
        width: 140,
        Cell: ({ value }: *) => (value ? moment(value).format('MM/DD/YYYY') : '—'),
      },
      {
        Header: i18n.t('Shipped'),
        accessor: 'node.actualShipDate',
        width: 130,
        Cell: ({ value }: *) => (value ? moment(value).format('MM/DD/YYYY') : '—'),
      },
      {
        Header: i18n.t('Created'),
        accessor: 'node.createdAt',
        width: 130,
        Cell: ({ value }: *) => moment(value).format('MM/DD/YYYY'),
      },
    ];

    return columns;
  };

  render() {
    const { shipmentEdges, deleteEnabled, onCreateNew } = this.props;

    if (!shipmentEdges || shipmentEdges.length === 0) {
      return null;
    }

    return (
      <Wrapper>
        <Table
          data={shipmentEdges}
          columns={this.getColumns()}
          onRowClick={this.handleRowClick}
          contextMenuItems={
            deleteEnabled
              ? [
                  {
                    label: i18n.t('Delete'),
                    onClick: (row: Object) => this.handleDelete(row.node),
                  },
                ]
              : []
          }
        />
      </Wrapper>
    );
  }
}

export default createFragmentContainer(ShipmentsTable, {
  shipmentEdges: graphql`
    fragment ShipmentsTable_shipmentEdges on ShipmentEdge @relay(plural: true) {
      node {
        id
        shipmentNumber
        status
        carrierName
        trackingNumber
        shippingMethod
        estimatedShipDate
        actualShipDate
        estimatedDeliveryDate
        actualDeliveryDate
        notes
        order {
          id
          orderNumber
        }
        customer {
          id
          name
        }
        createdAt
        updatedAt
      }
    }
  `,
});
